/**
 * Terrain mesh (GDD §3.3; concept L262). Columns are packed toward the river with a cubic distribution;
 * colours come from the world profile's palette and slope.
 */
import { BufferAttribute, BufferGeometry, Color } from 'three';
import { lerp } from '../core/math.ts';
import type { WorldProfile } from './profiles.ts';

/** Metres per ground detail-texture repeat, across and along the river (concept L262). */
export const GROUND_UV = 14;

export function buildTerrain(
  W: WorldProfile,
  z0: number,
  z1: number,
  nx: number,
  nz: number,
  halfW: number,
): BufferGeometry {
  const nv = (nx + 1) * (nz + 1);
  const pos = new Float32Array(nv * 3);
  const cl = new Float32Array(nv * 3);
  const uv = new Float32Array(nv * 2);
  const tmp = new Color();
  const e = 1.0;
  let i = 0;
  for (let iz = 0; iz <= nz; iz++) {
    const z = lerp(z0, z1, iz / nz);
    const c = W.center(z);
    const w = W.width(z);
    for (let ix = 0; ix <= nx; ix++) {
      const u = (ix / nx) * 2 - 1;
      const x = c + halfW * (0.45 * u + 0.55 * u * u * u);
      const h = W.height(x, z);
      const gx = W.height(x + e, z) - h;
      const gz = W.height(x, z + e) - h;
      W.color(tmp, h, Math.sqrt(gx * gx + gz * gz) / e, Math.abs(x - c) - w, x, z);
      pos[i * 3] = x;
      pos[i * 3 + 1] = h;
      pos[i * 3 + 2] = z;
      cl[i * 3] = tmp.r;
      cl[i * 3 + 1] = tmp.g;
      cl[i * 3 + 2] = tmp.b;
      uv[i * 2] = x / GROUND_UV;
      uv[i * 2 + 1] = z / GROUND_UV;
      i++;
    }
  }
  const idx = new Uint32Array(nx * nz * 6);
  let k = 0;
  for (let iz = 0; iz < nz; iz++)
    for (let ix = 0; ix < nx; ix++) {
      const a = iz * (nx + 1) + ix;
      const b = a + 1;
      const cc = a + nx + 1;
      const dd = cc + 1;
      idx[k++] = a;
      idx[k++] = b;
      idx[k++] = cc;
      idx[k++] = b;
      idx[k++] = dd;
      idx[k++] = cc;
    }
  const geo = new BufferGeometry();
  geo.setAttribute('position', new BufferAttribute(pos, 3));
  geo.setAttribute('color', new BufferAttribute(cl, 3));
  geo.setAttribute('uv', new BufferAttribute(uv, 2));
  geo.setIndex(new BufferAttribute(idx, 1));
  geo.computeVertexNormals();
  geo.computeBoundingSphere();
  return geo;
}

/**
 * The terrain strip split into z-chunks that frustum culling can skip (GDD §10.3). Normals are computed on the
 * whole strip first, so shared rows get identical normals and chunk seams are invisible.
 */
export function buildTerrainChunks(
  W: WorldProfile,
  z0: number,
  z1: number,
  nx: number,
  nz: number,
  halfW: number,
  chunkLength: number,
  /** Adjusts the whole strip before it is cut (the concept land, D1-look), so chunk seams stay identical. */
  finish?: (full: BufferGeometry) => void,
): BufferGeometry[] {
  const full = buildTerrain(W, z0, z1, nx, nz, halfW);
  finish?.(full);
  const pos = full.getAttribute('position').array as Float32Array;
  const nor = full.getAttribute('normal').array as Float32Array;
  const col = full.getAttribute('color').array as Float32Array;
  const uvs = full.getAttribute('uv').array as Float32Array;
  const rowsPerChunk = Math.max(1, Math.round((chunkLength / (z0 - z1)) * nz));
  const cols = nx + 1;
  const chunks: BufferGeometry[] = [];
  for (let r0 = 0; r0 < nz; r0 += rowsPerChunk) {
    const r1 = Math.min(nz, r0 + rowsPerChunk);
    const rows = r1 - r0 + 1;
    const from = r0 * cols;
    const to = (r1 + 1) * cols;
    const g = new BufferGeometry();
    g.setAttribute('position', new BufferAttribute(pos.slice(from * 3, to * 3), 3));
    g.setAttribute('normal', new BufferAttribute(nor.slice(from * 3, to * 3), 3));
    g.setAttribute('color', new BufferAttribute(col.slice(from * 3, to * 3), 3));
    g.setAttribute('uv', new BufferAttribute(uvs.slice(from * 2, to * 2), 2));
    const idx = new Uint32Array((rows - 1) * nx * 6);
    let k = 0;
    for (let iz = 0; iz < rows - 1; iz++)
      for (let ix = 0; ix < nx; ix++) {
        const a = iz * cols + ix;
        const b = a + 1;
        const cc = a + cols;
        const dd = cc + 1;
        idx[k++] = a;
        idx[k++] = b;
        idx[k++] = cc;
        idx[k++] = b;
        idx[k++] = dd;
        idx[k++] = cc;
      }
    g.setIndex(new BufferAttribute(idx, 1));
    g.computeBoundingSphere();
    chunks.push(g);
  }
  full.dispose();
  return chunks;
}
